import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom-v5-compat';
import {
  VaCheckbox,
  VaButtonPair,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import Wrapper from '../layout/Wrapper';
import {
  setSelectedTopics,
  selectSelectedTopics,
} from '../redux/slices/formSlice';
import { useGetTopicsQuery } from '../redux/api/vassApi';
import { useErrorFocus } from '../hooks/useErrorFocus';
import { sortTopicsAlphabetically } from '../utils/appointments';
import { URLS } from '../utils/constants';
import { pathWithSearch } from '../utils/invitationQuery';
import { isServerError } from '../utils/errors';

const TopicSelection = () => {
  const [{ error, handleSetError }] = useErrorFocus(['va-checkbox-group']);
  const dispatch = useDispatch();
  const selectedTopics = useSelector(selectSelectedTopics);
  const navigate = useNavigate();
  const { search } = useLocation();
  const { data, isLoading: loading, error: topicsError } = useGetTopicsQuery();
  const topics = useMemo(() => sortTopicsAlphabetically(data?.topics), [data]);

  const handleTopicChange = event => {
    handleSetError('');
    const { checked } = event.detail;
    if (checked) {
      const newTopics = [
        ...selectedTopics,
        topics.find(topic => topic.topicId === event.target.value),
      ];
      dispatch(setSelectedTopics(newTopics));
    } else {
      const newTopics = selectedTopics.filter(
        topic => topic.topicId !== event.target.value,
      );
      dispatch(setSelectedTopics(newTopics));
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleContinue = () => {
    if (!selectedTopics?.length) {
      handleSetError('Choose a topic for your appointment');
      return;
    }
    navigate(pathWithSearch(URLS.REVIEW, search));
  };

  return (
    <Wrapper
      pageTitle="What do you want to learn more about?"
      showBackLink
      required
      loading={loading}
      loadingMessage="Loading topics. This may take up to 30 seconds. Please don’t refresh the page."
      errorAlert={isServerError(topicsError)}
    >
      <va-checkbox-group
        data-testid="topic-checkbox-group"
        label="Check all that apply"
        class="vass-checkbox-group"
        error={error}
      >
        {topics.map(({ topicId, topicName }) => (
          <VaCheckbox
            key={topicId}
            data-testid={`topic-checkbox-${topicId
              .toLowerCase()
              .replace(/\s+/g, '-')}`}
            label={topicName}
            name="topic"
            value={topicId}
            onVaChange={handleTopicChange}
            checked={(selectedTopics || []).some(
              topic => topic.topicId === topicId,
            )}
          />
        ))}
      </va-checkbox-group>
      <VaButtonPair
        data-testid="button-pair"
        continue
        onPrimaryClick={handleContinue}
        onSecondaryClick={handleBack}
        class="vads-u-margin-top--4"
      />
    </Wrapper>
  );
};

export default TopicSelection;
